import { Link } from 'react-router-dom';
import { STANDOUT_FEATURES as STANDOUTS, ESSENTIAL_FEATURES as ESSENTIALS } from '../../data/navigation';

const ICON_CLASS = 'w-7 h-7 text-[var(--landing-accent-text)]';

interface StandoutCopy {
  tag: string;
  title: string;
  description: string;
  highlights: string[];
  icon: JSX.Element;
}

/** Homepage copy for each standout feature, keyed by its feature page href. */
const STANDOUT_COPY: Record<string, StandoutCopy> = {
  '/features/leasing': {
    tag: 'AI assistant',
    title: 'Fill vacancies without living in your inbox',
    description:
      'Write a listing once and post it to Craigslist and Facebook Marketplace with the Resida browser extension. Every inquiry lands in one pipeline, and the AI leasing assistant answers prospect questions and sends showing links.',
    highlights: [
      'One-click Craigslist and Facebook Marketplace posts',
      'Lead pipeline from first message to signed lease',
      'AI answers prospects and brings you the judgment calls',
    ],
    icon: (
      <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    ),
  },
  '/features/maintenance': {
    tag: 'AI assistant',
    title: 'Repairs that coordinate themselves',
    description:
      'Tenants report problems with photos. The AI repair coordinator triages each request, contacts a vendor from your list, schedules the visit, and keeps the tenant updated.',
    highlights: [
      'Photo-based repair requests from tenants',
      'Vendor outreach and scheduling from your own vendor list',
      'You decide anything that needs your judgment',
    ],
    icon: (
      <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75a4.5 4.5 0 0 1-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 1 1-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 0 1 6.336-4.486l-3.276 3.276a3.004 3.004 0 0 0 2.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852Z" />
      </svg>
    ),
  },
  '/features/rent-collection': {
    tag: 'Works with Zelle',
    title: 'Rent tracking that works with Zelle',
    description:
      'Your tenants keep paying the way they already do. Resida reads your bank feed, matches Zelle, Venmo, and bank transfers to the right lease, and flags anyone who is late.',
    highlights: [
      'Zelle, Venmo, Cash App, and transfer detection',
      'Learns each tenant\'s payment name',
      'Late flags, late fees, and reminders',
    ],
    icon: (
      <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
  },
  '/features/expense-tracking': {
    tag: 'Email in, filed out',
    title: 'Forward a receipt, and it files itself',
    description:
      'Forward receipts and invoices to your Resida email address. AI pulls out the details, files each expense under the right property and unit, keeps the original, and assigns the IRS Schedule E category.',
    highlights: [
      'Matched to the right property and unit automatically',
      'Original email and attachments kept with every expense',
      'Schedule E categories and tax-ready reports',
    ],
    icon: (
      <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
      </svg>
    ),
  },
  '/features/ai-assistants': {
    tag: 'Claude & ChatGPT',
    title: 'Ask Claude or ChatGPT about your rentals',
    description:
      'Connect Claude or ChatGPT to your Resida account and ask in plain English: who is behind on rent, what a property cost you this year, what needs you today.',
    highlights: [
      'Answers from your live Resida data',
      'Approve late fees and match payments in chat',
      'You choose what it can see and do',
    ],
    icon: (
      <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
      </svg>
    ),
  },
};

export function Features(): JSX.Element {
  return (
    <section
      id="features"
      data-testid="landing-features"
      className="py-[var(--landing-section-py)] bg-[var(--landing-bg)]"
    >
      <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--landing-text-primary)] mb-4">
            {STANDOUTS.headline}
          </h2>
          <p className="text-lg text-[var(--landing-text-muted)] max-w-2xl mx-auto">
            Every landlord app stores leases and collects rent. Resida also does the work most of
            them leave to you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {STANDOUTS.items.map((item, index) => {
            const copy = STANDOUT_COPY[item.href];
            if (!copy) return null;
            // An odd last card spans both columns so the grid ends evenly.
            const spansRow = index === STANDOUTS.items.length - 1 && STANDOUTS.items.length % 2 === 1;
            return (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`feature-standout-${item.href.split('/').pop()}`}
                className={`bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-8 border border-[var(--landing-border)] hover:border-[var(--landing-accent)] transition-colors group flex flex-col${spansRow ? ' md:col-span-2' : ''}`}
              >
                <div className="flex items-center justify-between mb-4">
                  {copy.icon}
                  <span className="text-xs font-medium uppercase tracking-widest text-[var(--landing-accent-text)]">
                    {copy.tag}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-[var(--landing-text-primary)] group-hover:text-[var(--landing-accent-text)] transition-colors mb-3">
                  {copy.title}
                </h3>
                <p className="text-[var(--landing-text-muted)] leading-relaxed mb-4">
                  {copy.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {copy.highlights.map((text) => (
                    <li key={text} className="flex items-start gap-3 text-sm text-[var(--landing-text-secondary)]">
                      <span className="text-[var(--landing-accent-text)] font-bold flex-shrink-0 mt-0.5" aria-hidden="true">
                        &#10003;
                      </span>
                      {text}
                    </li>
                  ))}
                </ul>
                <span className="mt-auto text-sm text-[var(--landing-accent-text)] font-medium">
                  See how it works &rarr;
                </span>
              </Link>
            );
          })}
        </div>

        <div className="max-w-4xl mx-auto">
          <h3 className="text-xl font-semibold text-[var(--landing-text-primary)] text-center mb-2">
            Plus {ESSENTIALS.headline.toLowerCase()}
          </h3>
          <p className="text-[var(--landing-text-muted)] text-center max-w-2xl mx-auto mb-8">
            {ESSENTIALS.summary}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {ESSENTIALS.items.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="block w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc((100%-2rem)/3)] p-4 rounded-[var(--landing-radius)] border border-[var(--landing-border)] hover:border-[var(--landing-accent)] transition-colors group"
              >
                <div className="text-sm font-medium text-[var(--landing-text-primary)] group-hover:text-[var(--landing-accent-text)] transition-colors">
                  {item.label}
                </div>
                <div className="text-xs text-[var(--landing-text-muted)] mt-1">{item.description}</div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/features"
              className="text-sm text-[var(--landing-accent-text)] font-medium hover:text-[var(--landing-accent-text-hover)] transition-colors"
            >
              See all features &rarr;
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
